import { useState } from "react";
import { DndContext, DragOverlay, useDroppable, useDraggable, closestCenter, pointerWithin } from "@dnd-kit/core";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { GripVertical, User } from "lucide-react";

function DraggableCard({ operator }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: operator.user_id,
    data: { operator },
  });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2.5 p-2.5 bg-white border border-[#E5E7EB] cursor-grab active:cursor-grabbing transition-all duration-150
        ${isDragging ? "opacity-40 scale-95" : "hover:border-[#002FA7]/40 hover:-translate-y-0.5"}`}
      data-testid={`dnd-operator-${operator.user_id}`}
      {...listeners}
      {...attributes}
    >
      <GripVertical size={14} className="text-[#9CA3AF] shrink-0" />
      <Avatar className="h-7 w-7 rounded-none shrink-0">
        <AvatarImage src={operator.picture} />
        <AvatarFallback className="rounded-none bg-[#002FA7] text-white text-[10px] font-bold">
          {operator.name?.charAt(0)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-[#0A0A0A] truncate">{operator.name}</p>
        <div className="flex gap-0.5 mt-0.5">
          {(operator.languages || []).slice(0, 3).map((l) => (
            <span key={l} className="text-[8px] font-bold uppercase px-1 py-0 bg-[#F1F3F5] text-[#4B5563]">{l}</span>
          ))}
        </div>
      </div>
      {operator.is_online && (
        <span className="w-2 h-2 rounded-full bg-[#00C853] shrink-0" title="Online" />
      )}
    </div>
  );
}

function DroppableColumn({ id, title, count, operators, accentColor }) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={`min-w-[220px] max-w-[260px] flex-1 border transition-colors duration-200
        ${isOver ? "border-[#002FA7] bg-[#002FA7]/5" : "border-[#E5E7EB] bg-white"}`}
      data-testid={`dnd-column-${id}`}
    >
      <div className="p-3 border-b border-[#E5E7EB]" style={{ borderTopWidth: "3px", borderTopColor: accentColor || "#E5E7EB" }}>
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-bold tracking-[0.1em] uppercase text-[#0A0A0A] truncate">{title}</h4>
          <Badge variant="outline" className="rounded-none text-[10px] font-bold h-5 px-1.5">{count}</Badge>
        </div>
      </div>
      <div className="p-2 space-y-1.5 min-h-[120px] max-h-[400px] overflow-y-auto thin-scrollbar">
        {operators.map((op) => (
          <DraggableCard key={op.user_id} operator={op} />
        ))}
        {operators.length === 0 && (
          <div className="flex items-center justify-center h-20 border border-dashed border-[#E5E7EB]">
            <p className="text-[10px] text-[#9CA3AF] tracking-[0.1em] uppercase">Drop here</p>
          </div>
        )}
      </div>
    </div>
  );
}

function OverlayCard({ operator }) {
  if (!operator) return null;
  return (
    <div className="flex items-center gap-2.5 p-2.5 bg-white border-2 border-[#002FA7] shadow-[4px_4px_0_0_rgba(0,47,167,0.3)] cursor-grabbing w-[220px]">
      <User size={14} className="text-[#002FA7]" />
      <p className="text-xs font-bold text-[#0A0A0A]">{operator.name}</p>
    </div>
  );
}

export default function DndOperatorBoard({ projects, operators, onAssign, onUnassign, t }) {
  const [activeOp, setActiveOp] = useState(null);

  // Build assignment map: project_id -> [operators]
  const assigned = {};
  const unassigned = [];
  for (const p of projects) {
    assigned[p.project_id] = [];
  }
  for (const op of operators) {
    const projs = op.assigned_projects || [];
    if (projs.length === 0) {
      unassigned.push(op);
    } else {
      for (const pid of projs) {
        if (assigned[pid]) {
          assigned[pid].push(op);
        } else {
          unassigned.push(op);
        }
      }
    }
  }

  const findOperatorSource = (opId) => {
    const op = operators.find((o) => o.user_id === opId);
    if (!op) return null;
    const projs = op.assigned_projects || [];
    return projs.length > 0 ? projs[0] : "unassigned";
  };

  const handleDragStart = (event) => {
    const op = event.active.data.current?.operator;
    setActiveOp(op);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveOp(null);
    if (!over) return;

    const operatorId = active.id;
    const targetProjectId = over.id;
    const sourceProjectId = findOperatorSource(operatorId);

    if (sourceProjectId === targetProjectId) return;

    // Unassign from source if it was a project
    if (sourceProjectId && sourceProjectId !== "unassigned") {
      onUnassign(sourceProjectId, operatorId);
    }
    // Assign to target if it's a project
    if (targetProjectId !== "unassigned") {
      onAssign(targetProjectId, operatorId);
    }
  };

  return (
    <div data-testid="dnd-operator-board">
      <p className="text-xs text-[#9CA3AF] mb-3 tracking-[0.1em] uppercase font-bold">
        {t ? t("dnd.drag_hint") : "Drag operators between projects for instant reassignment"}
      </p>
      <DndContext
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-4 overflow-x-auto pb-4">
          <DroppableColumn
            id="unassigned"
            title={t ? t("dnd.unassigned") : "Unassigned"}
            count={unassigned.length}
            operators={unassigned}
            accentColor="#9CA3AF"
          />
          {projects.map((p, i) => (
            <DroppableColumn
              key={p.project_id}
              id={p.project_id}
              title={p.name}
              count={(assigned[p.project_id] || []).length}
              operators={assigned[p.project_id] || []}
              accentColor={["#002FA7", "#0A0A0A", "#00C853", "#FFB800"][i % 4]}
            />
          ))}
        </div>
        <DragOverlay>
          <OverlayCard operator={activeOp} />
        </DragOverlay>
      </DndContext>
    </div>
  );
}
